import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { ILink } from '../caremap/WithLinks';

// Mirrors the API's AI call log source union (innerhive-api ai/call-logs/model.ts). Drives the
// source filter dropdown; an unrecognized value from a newer API still renders (it's a string
// at runtime), it just won't be offered as a filter until this list catches up.
export const AI_CALL_SOURCES = [
    'chat', 'chat-continuation', 'intent-gate', 'chat-memory-extraction',
    'recording-transcribe', 'recording-chunk-transcribe', 'recording-summary',
    'recording-memory-extraction', 'memory-gardener', 'reflection',
    'attachment-usefulness', 'medication-parse', 'welcome-email', 'eval-judge',
    'dev-script'
] as const;

export type AICallSource = typeof AI_CALL_SOURCES[number];

export type AICallStatus = 'ok' | 'incomplete' | 'error';

export interface IAICallPayload {
    provider: string;
    model: string;
    source: AICallSource;
    request: {
        instructions?: string;
        input: unknown;
        tools?: unknown;
    };
    response?: {
        outputItems?: unknown;
        outputText?: string;
        usage?: unknown;
    };
    error?: string;
}

export interface IAICallSummary {
    callId: string;
    createdAt: string;
    userId: string;
    userEmail?: string;
    source: AICallSource;
    provider: string;
    model: string;
    status: AICallStatus;
    latencyMs: number;
    inputTokens?: number;
    outputTokens?: number;
    toolNames?: string[];
    inputPreview?: string;
    outputPreview?: string;
    errorMessage?: string;
    isTestUser: boolean;
    careMapId?: string;
    correlationKey?: string;
    links: { detail: ILink; conversation?: ILink; byUser: ILink };
}

export interface IAICallDetail extends IAICallSummary {
    payload?: IAICallPayload;
    payloadMissing?: boolean;
}

export interface IAICallListResponse {
    calls: IAICallSummary[];
    links: { next?: ILink };
}

export interface IAIConversationResponse {
    correlationKey: string;
    calls: IAICallDetail[];
}

export interface IAILogsFilters {
    source?: AICallSource;
    includeTestUsers: boolean;
}

export class AILogsClient {
    static inject = () => [APIClient];
    constructor(private apiClient: APIClient) {}

    getRecent = async (filters: IAILogsFilters): Promise<IAICallListResponse> => {
        const entry = await this.apiClient.entry();
        return await this.apiClient.get(entry.links.aiCallLogs, undefined, {
            source: filters.source,
            includeTestUsers: String(filters.includeTestUsers)
        });
    };

    getPage = async (link: ILink): Promise<IAICallListResponse> => {
        return await this.apiClient.get(link);
    };

    getConversation = async (link: ILink): Promise<IAIConversationResponse> => {
        return await this.apiClient.get(link, undefined, { hydrate: 'true' });
    };

    getCall = async (link: ILink): Promise<IAICallDetail> => {
        return await this.apiClient.get(link);
    };
}
